import React, { useState, useEffect } from 'react';
import activityLogService from '../../services/activityLogService';
import { authService } from '../../services/authService';
import Toast from '../../components/Common/Toast';
import { formatDateLocaleLima, formatTimeToLima } from '../../utils/dateUtils';

const ACTION_LABELS = { created: 'Creación', updated: 'Edición', deleted: 'Eliminación' };
const ACTION_COLORS = { created: 'bg-green-100 text-green-800', updated: 'bg-blue-100 text-blue-800', deleted: 'bg-red-100 text-red-800' };

const FIELD_LABELS = {
  unit_price: 'Precio unit.',
  quantity: 'Cantidad',
  total: 'Total',
  subtotal: 'Subtotal',
  tax_amount: 'IGV',
  tax_rate: 'Tasa impuesto',
  description: 'Descripción',
  progress_date: 'Fecha avance',
  item_date: 'Fecha ítem',
  issue_date: 'Fecha emisión',
  entry_date: 'Fecha ingreso',
  updated_at: 'Última actualización',
  updated_by: 'Actualizado por',
  created_at: 'Fecha creación',
  created_by: 'Creado por',
  deleted_by: 'Eliminado por',
  name: 'Nombre',
  code: 'Código',
  status: 'Estado',
  document_type: 'Tipo documento',
  warehouse_id: 'Almacén',
  project_id: 'Proyecto',
  product_id: 'Producto',
  service_id: 'Servicio',
  document_id: 'Documento',
  supplier_id: 'Proveedor',
  area_id: 'Área',
  activo_family_id: 'Familia de activo',
  acquisition_date: 'Fecha de adquisición',
  acquisition_quantity: 'Cantidad adquirida',
  acquisition_unit_price: 'Precio unitario de adquisición',
  acquisition_currency: 'Moneda de adquisición',
  acquisition_base_amount: 'Monto base de adquisición',
  activity_start_date: 'Inicio de actividades',
  useful_life_years: 'Vida útil (años)',
  annual_depreciation: 'Depreciación anual',
  monthly_depreciation: 'Depreciación mensual',
  retired_at: 'Fecha de baja',
  retirement_notes: 'Notas de baja',
  brand: 'Marca',
  model: 'Modelo',
  serial_number: 'Número de serie',
  machinery_asset_type: 'Tipo de maquinaria',
  machinery_asset_id: 'ID de maquinaria',
  notes: 'Notas',
  is_active: 'Activo',
};

const MODEL_LABELS = {
  ConcesionariaProductionGuideLink: 'Vinculación de guías de producción',
  ConcesionariaFinishedWarehouseConfig: 'Configuración de almacén de producto terminado',
  EntryGuide: 'Guía',
  EntryGuideItem: 'Ítem de guía',
  KardexMovement: 'Movimiento de kardex',
  Ciclico: 'Cíclico',
  Activo: 'Activo',
  ActivoCategory: 'Categoría de activo',
  ActivoSubcategory: 'Subcategoría de activo',
  ActivoFamily: 'Familia de activo',
};

const prettifyModelName = (name = '') => {
  if (!name) return '';
  if (MODEL_LABELS[name]) return MODEL_LABELS[name];
  return name
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/_/g, ' ')
    .trim();
};

const formatLogDescription = (description) => {
  if (!description) return '—';
  return Object.entries(MODEL_LABELS).reduce((text, [rawName, friendlyName]) => {
    const regex = new RegExp(rawName, 'g');
    return text.replace(regex, friendlyName);
  }, description);
};

const formatValue = (value) => {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'boolean') return value ? 'Sí' : 'No';
  if (typeof value === 'object') return JSON.stringify(value);
  const s = String(value);
  if (/^\d{4}-\d{2}-\d{2}T?\d{2}:\d{2}/.test(s)) {
    try {
      const d = new Date(s);
      if (!isNaN(d.getTime())) return `${formatDateLocaleLima(d)} ${formatTimeToLima(d)}`;
    } catch (_) {}
    return s;
  }
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) {
    try {
      const d = new Date(s);
      if (!isNaN(d.getTime())) return formatDateLocaleLima(d);
    } catch (_) {}
    return s;
  }
  return s;
};

const getChangedFields = (oldVal, newVal) => {
  if (!oldVal || !newVal || typeof oldVal !== 'object' || typeof newVal !== 'object') return [];
  const keys = new Set([...Object.keys(oldVal), ...Object.keys(newVal)]);
  const changed = [];
  keys.forEach((key) => {
    const o = oldVal[key];
    const n = newVal[key];
    const so = o === null || o === undefined ? '' : String(o);
    const sn = n === null || n === undefined ? '' : String(n);
    if (so !== sn && JSON.stringify(o) !== JSON.stringify(n)) changed.push({ key, old: o, new: n });
  });
  return changed;
};

const ActivityLogs = () => {
  const currentUser = authService.getCurrentUser();
  const isLimitedUser = currentUser?.role === 'limitado';

  const [logs, setLogs] = useState([]);
  const [meta, setMeta] = useState({ current_page: 1, last_page: 1, per_page: 20, total: 0 });
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState({ isOpen: false, message: '', type: 'info' });

  const [filters, setFilters] = useState({
    search: '',
    action: '',
    loggable_type: '',
    date_from: '',
    date_to: '',
    page: 1,
    per_page: 20,
  });

  const [selectedLog, setSelectedLog] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const loadLogs = async (override = {}) => {
    setLoading(true);
    const f = { ...filters, ...override };
    try {
      const params = { page: f.page, per_page: f.per_page };
      if (f.search) params.search = f.search;
      if (f.action) params.action = f.action;
      if (f.loggable_type) params.loggable_type = f.loggable_type;
      if (f.date_from) params.date_from = f.date_from;
      if (f.date_to) params.date_to = f.date_to;

      const response = await activityLogService.getLogs(params);
      if (response.success) {
        setLogs(response.data || []);
        setMeta(response.meta || meta);
      } else {
        setToast({ isOpen: true, message: response.message || 'Error al cargar logs', type: 'error' });
      }
    } catch (error) {
      console.error('Error al cargar logs:', error);
      setToast({ isOpen: true, message: 'Error al cargar el registro de actividad', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadLogs();
  }, [filters.page, filters.per_page]);

  const handleFilter = (e) => {
    e.preventDefault();
    setFilters((prev) => ({ ...prev, page: 1 }));
    loadLogs({ page: 1 });
  };

  const handleViewDetail = async (log) => {
    setDetailLoading(true);
    setSelectedLog(null);
    try {
      const response = await activityLogService.getLog(log.id);
      if (response.success) setSelectedLog(response.data);
    } finally {
      setDetailLoading(false);
    }
  };

  const loggableTypeShort = (type) => prettifyModelName(type ? type.replace(/^App\\Models\\/, '') : '');

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-800">Registro de actividad</h1>
        <p className="text-gray-600 mt-1">
          {isLimitedUser
            ? 'Historial de sus creaciones, ediciones y eliminaciones en el sistema.'
            : 'Trazabilidad de quién crea, edita y elimina en el sistema.'}
        </p>
      </div>

      <form onSubmit={handleFilter} className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 items-end">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Buscar</label>
            <input
              type="text"
              value={filters.search}
              onChange={(e) => setFilters((prev) => ({ ...prev, search: e.target.value }))}
              placeholder={isLimitedUser ? 'Buscar en descripción...' : 'Usuario o descripción...'}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Acción</label>
            <select
              value={filters.action}
              onChange={(e) => setFilters((prev) => ({ ...prev, action: e.target.value }))}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
            >
              <option value="">Todas</option>
              <option value="created">Creación</option>
              <option value="updated">Edición</option>
              <option value="deleted">Eliminación</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Desde</label>
            <input
              type="date"
              value={filters.date_from}
              onChange={(e) => setFilters((prev) => ({ ...prev, date_from: e.target.value }))}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Hasta</label>
            <input
              type="date"
              value={filters.date_to}
              onChange={(e) => setFilters((prev) => ({ ...prev, date_to: e.target.value }))}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
            />
          </div>
          <div className="flex gap-2">
            <button type="submit" className="px-4 py-2 bg-teal-600 text-white rounded-md text-sm font-medium hover:bg-teal-700">
              Filtrar
            </button>
            <button
              type="button"
              onClick={() => {
                const cleared = { search: '', action: '', loggable_type: '', date_from: '', date_to: '', page: 1, per_page: 20 };
                setFilters(cleared);
                loadLogs(cleared);
              }}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Limpiar
            </button>
          </div>
        </div>
      </form>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-gray-500">Cargando...</div>
        ) : logs.length === 0 ? (
          <div className="p-12 text-center text-gray-500">No hay registros de actividad con los filtros seleccionados.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fecha y hora</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Usuario</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Acción</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Descripción</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Detalle</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {logs.map((log) => (
                  <tr key={log.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">
                      {log.created_at ? (
                        <>
                          {formatDateLocaleLima(log.created_at)} {formatTimeToLima(log.created_at)}
                        </>
                      ) : ''}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900">
                      {log.user ? log.user.name : <span className="text-gray-400">—</span>}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${ACTION_COLORS[log.action] || 'bg-gray-100 text-gray-800'}`}>
                        {ACTION_LABELS[log.action] || log.action}
                      </span>
                    </td>
                    <td
                      className="px-4 py-3 text-sm text-gray-600 max-w-xs truncate"
                      title={formatLogDescription(log.description)}
                    >
                      {formatLogDescription(log.description)}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        type="button"
                        onClick={() => handleViewDetail(log)}
                        className="text-teal-600 hover:text-teal-800 text-sm font-medium"
                      >
                        Ver
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {meta.last_page > 1 && (
          <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between">
            <p className="text-sm text-gray-600">
              Mostrando página {meta.current_page} de {meta.last_page} ({meta.total} registros)
            </p>
            <div className="flex gap-2">
              <button
                type="button"
                disabled={meta.current_page <= 1}
                onClick={() => setFilters((prev) => ({ ...prev, page: prev.page - 1 }))}
                className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50"
              >
                Anterior
              </button>
              <button
                type="button"
                disabled={meta.current_page >= meta.last_page}
                onClick={() => setFilters((prev) => ({ ...prev, page: prev.page + 1 }))}
                className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50"
              >
                Siguiente
              </button>
            </div>
          </div>
        )}
      </div>

      {selectedLog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setSelectedLog(null)}>
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Detalle del registro</h3>
              {detailLoading ? (
                <p className="text-gray-500">Cargando...</p>
              ) : (
                <>
                  <dl className="space-y-3 text-sm mb-4">
                    <div><dt className="text-gray-500">Fecha y hora</dt><dd className="font-medium">{selectedLog.created_at ? `${formatDateLocaleLima(selectedLog.created_at)} ${formatTimeToLima(selectedLog.created_at)}` : '—'}</dd></div>
                    <div><dt className="text-gray-500">Usuario</dt><dd className="font-medium">{selectedLog.user ? selectedLog.user.name : '—'} {selectedLog.user?.email && <span className="text-gray-500">({selectedLog.user.email})</span>}</dd></div>
                    <div><dt className="text-gray-500">Acción</dt><dd><span className={`px-2 py-0.5 rounded text-xs font-medium ${ACTION_COLORS[selectedLog.action] || 'bg-gray-100'}`}>{ACTION_LABELS[selectedLog.action] || selectedLog.action}</span></dd></div>
                    <div><dt className="text-gray-500">Entidad</dt><dd className="font-medium">{loggableTypeShort(selectedLog.loggable_type)} #{selectedLog.loggable_id}</dd></div>
                    <div><dt className="text-gray-500">Descripción</dt><dd className="text-gray-700">{formatLogDescription(selectedLog.description)}</dd></div>
                  </dl>

                  {selectedLog.action === 'updated' && selectedLog.old_values && selectedLog.new_values && (() => {
                    const changes = getChangedFields(selectedLog.old_values, selectedLog.new_values);
                    if (changes.length === 0) return null;
                    return (
                      <div className="mb-4">
                        <h4 className="text-sm font-semibold text-gray-700 mb-2">Cambios realizados</h4>
                        <div className="border border-gray-200 rounded-lg overflow-hidden">
                          <table className="min-w-full text-sm">
                            <thead className="bg-gray-50">
                              <tr>
                                <th className="px-3 py-2 text-left font-medium text-gray-600">Campo</th>
                                <th className="px-3 py-2 text-left font-medium text-gray-600">Antes</th>
                                <th className="px-3 py-2 text-left font-medium text-gray-600">Después</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                              {changes.map(({ key, old: o, new: n }) => (
                                <tr key={key} className="hover:bg-gray-50/50">
                                  <td className="px-3 py-2 text-gray-700 font-medium">{FIELD_LABELS[key] || key}</td>
                                  <td className="px-3 py-2 text-red-700 bg-red-50/50">{formatValue(o)}</td>
                                  <td className="px-3 py-2 text-green-700 bg-green-50/50">{formatValue(n)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    );
                  })()}

                  {(selectedLog.action === 'created' && selectedLog.new_values && Object.keys(selectedLog.new_values).length > 0) && (
                    <div className="mb-4">
                      <h4 className="text-sm font-semibold text-gray-700 mb-2">Datos registrados</h4>
                      <div className="border border-gray-200 rounded-lg overflow-hidden">
                        <table className="min-w-full text-sm">
                          <tbody className="divide-y divide-gray-100">
                            {Object.entries(selectedLog.new_values).map(([key, value]) => (
                                <tr key={key} className="hover:bg-gray-50/50">
                                  <td className="px-3 py-2 text-gray-600 font-medium w-1/3">{FIELD_LABELS[key] || key}</td>
                                  <td className="px-3 py-2 text-gray-900">{formatValue(value)}</td>
                                </tr>
                              ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {(selectedLog.action === 'deleted' && selectedLog.old_values && Object.keys(selectedLog.old_values).length > 0) && (
                    <div className="mb-4">
                      <h4 className="text-sm font-semibold text-gray-700 mb-2">Datos del registro eliminado</h4>
                      <div className="border border-gray-200 rounded-lg overflow-hidden">
                        <table className="min-w-full text-sm">
                          <tbody className="divide-y divide-gray-100">
                            {Object.entries(selectedLog.old_values).map(([key, value]) => (
                              <tr key={key} className="hover:bg-gray-50/50">
                                <td className="px-3 py-2 text-gray-600 font-medium w-1/3">{FIELD_LABELS[key] || key}</td>
                                <td className="px-3 py-2 text-gray-900">{formatValue(value)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {((selectedLog.old_values && Object.keys(selectedLog.old_values).length > 0) || (selectedLog.new_values && Object.keys(selectedLog.new_values).length > 0)) && (
                    <details className="mt-2">
                      <summary className="text-sm text-gray-500 cursor-pointer hover:text-gray-700">Ver datos completos (JSON)</summary>
                      <div className="mt-2 space-y-2">
                        {selectedLog.old_values && Object.keys(selectedLog.old_values).length > 0 && (
                          <div>
                            <p className="text-xs text-gray-500 mb-1">Valores anteriores</p>
                            <pre className="bg-gray-50 p-3 rounded font-mono text-xs overflow-x-auto max-h-40 overflow-y-auto">{JSON.stringify(selectedLog.old_values, null, 2)}</pre>
                          </div>
                        )}
                        {selectedLog.new_values && Object.keys(selectedLog.new_values).length > 0 && (
                          <div>
                            <p className="text-xs text-gray-500 mb-1">Valores nuevos</p>
                            <pre className="bg-gray-50 p-3 rounded font-mono text-xs overflow-x-auto max-h-40 overflow-y-auto">{JSON.stringify(selectedLog.new_values, null, 2)}</pre>
                          </div>
                        )}
                      </div>
                    </details>
                  )}
                </>
              )}
              <div className="mt-6 flex justify-end">
                <button type="button" onClick={() => setSelectedLog(null)} className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium hover:bg-gray-50">Cerrar</button>
              </div>
            </div>
          </div>
        </div>
      )}

      <Toast isOpen={toast.isOpen} message={toast.message} type={toast.type} onClose={() => setToast((t) => ({ ...t, isOpen: false }))} />
    </div>
  );
};

export default ActivityLogs;
